import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import FormsService from '../services/forms-service/forms-service';
import { GetPatientResponse, LegacyFormSubmissionDto } from '../services/forms-service/forms-service-types';
import { EventForms } from '../types/event-bus';
import { useEventBus } from './use-event-bus';

export const useFormsService = () => {
  const { config } = useConfig();
  const oidc = useOidcWithAdmin();
  return useMemo(() => FormsService.getInstance(config, oidc.oidcService), [config, oidc.oidcService]);
};

export const useForms = (patientId?: number) => {
  const { listen } = useEventBus();

  const [forms, setForms] = useState<GetPatientResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const formsService = useFormsService();

  const load = useCallback(() => {
    if (!patientId) return;
    setIsLoading(true);
    formsService
      .getAllForms(patientId)
      .then(data => {
        setForms(data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [formsService, patientId]);

  useEffect(() => {
    load();
    const unsub = listen('forms', (data: any) => {
      const {
        data: { type }
      } = data as EventForms;
      if (type === 'reload') {
        console.log('reload');
        load();
      }
    });

    return () => {
      unsub();
    };
  }, [listen, load]);

  return { forms, isLoading, load, formsService };
};

export const getAge = (date?: string) => {
  if (!date) return null;

  return moment().diff(date, 'years');
};

export const useLegacyForms = (patientId?: number, locationId?: number) => {
  const [legacyForms, setLegacyForms] = useState<LegacyFormSubmissionDto[] | null>(null);
  const [isLegacyLoading, setIsLegacyLoading] = useState(false);

  const formsService = useFormsService();

  useEffect(() => {
    if (!patientId || !locationId) return;
    setIsLegacyLoading(true);
    formsService
      .getLegacySubmissions(patientId, locationId)
      .then(data => {
        setLegacyForms(data);
      })
      .catch(() => {
        setLegacyForms([]);
      })
      .finally(() => {
        setIsLegacyLoading(false);
      });
  }, [formsService, patientId]);

  return { legacyForms, isLegacyLoading, formsService };
};
